import { getConfiguration } from '@nuskin/configuration-sdk';
import { EquinoxService } from '@nuskin/ns-account';
import {RunConfigService, storage, UrlService, equinoxLocalStorage} from '@nuskin/ns-util';
import Favorite from './favorite';
import ServiceUtils from '../ServiceUtils';


const FAVORITE_RESPONSE_LIST = 'FavoriteResponseList';

/**
 * Promise representing the current GET request, if any. Null otherwise.
 * This object is used to prevent multiple simultaneous calls for the
 * favorites list from generating multiple simultaneous GET requests.
 * @type {Promise}
 * @ignore
 */
let favoritesListPromise = null;

/**
 * In-memory cached copy of the favorites list; this prevents multiple
 * function calls on a page from generating multiple network calls, even
 * if the favorites list cannot be persisted to local storage.
 * @type {Array.<Favorite>}
 * @ignore
 */
let favoritesList = null;

/**
 * The Favorite Service provides methods for accessing and maintaining the
 * user's list of favorite product SKUs.
 *
 * After it is first requested from the server, the user's favorite list is
 * stored in session storage for quick retrieval. It is updated after every
 * subsequent request to the server (e.g. when adding or removing items from
 * the favorites list).
 *
 * To clear the favorites list from session storage, e.g. on user logout,
 * call the function FavoritesService.clearLocalData().
 *
 * The Favorites Service can make requests to either the legacy shop-service
 * endpoints or the AWS cloud endpoints, depending on the settings in market
 * configuration.
 * @namespace FavoritesService
 */
export default {
    getFavorites,
    addToFavorites,
    removeFromFavorites,
    isInFavorites,
    clearLocalData,
    getFavoritesFromServer
};


//=================================================================//
//
//  PUBLIC FUNCTIONS
//
//=================================================================//

/**
 * Retrieves the user's list of favorites.
 * Results are preferentially retrieved from the session storage cache.
 * @param {Object} [options] - options:
 * @param {boolean} [options.doAtp = false] - Requests updated ATP information from the server
 * @param {boolean} [options.forceRefresh = false] - Forces a request from the server instead of from session storage cache
 * @returns {Promise.<Array.<Favorite>>} Promise that resolves to favorites list
 * @memberof FavoritesService
 * @public
 */
async function getFavorites(options) {
    const DEFAULT_OPTIONS = {
        doAtp: false,
        forceRefresh: false
    };
    options = Object.assign({}, DEFAULT_OPTIONS, options);

    let favorites = getFavoritesLocal();

    if (!favorites || options.doAtp || options.forceRefresh) {
        if (!favoritesListPromise) {
            favoritesListPromise = getFavoritesFromServer(options);
        }
        favorites = await favoritesListPromise;
        favoritesListPromise = null;
    }

    return favorites;
}

/**
 * Adds the given product SKU to the user's list of favorites.
 * Returns the updated favorites list.
 * @param {string} sku - SKU ID of product
 * @returns {Promise.<Array.<Favorite>>} Promise that resolves to favorites list
 * @memberof FavoritesService
 * @public
 */
async function addToFavorites(sku) {
    let url = `${getServiceUrl()}?cntry=${getCountry()}`;

    if (!isAwsMode()) {
        url += `&eid=${ServiceUtils.getUser().eid}`;
    }

    let headers = getHeaders();
    headers['Content-Type'] = 'application/json';

    let body = {'sku': sku};


    if (await isMarketEquinoxEnabled()) {
        localStorage.setItem('pendingFavorite', JSON.stringify({sku: sku}));
        await EquinoxService.getOrCreateFavorites()
        const equinoxFavoriteResponse = await EquinoxService.getEquinoxFavorite();
        const response = EquinoxService.equinoxFavoritesMapper(equinoxFavoriteResponse);
        return handleFavoritesListResponse(response);
    }
    
    const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        credentials: 'omit',
        body: JSON.stringify(body)
    });

    return handleFavoritesListResponse(response);
}

/**
 * Removes the given product SKU from the user's list of favorites.
 * Returns the updated favorites list.
 * @param {string} sku - SKU ID of product
 * @returns {Promise.<Array.<Favorite>>} Promise that resolves to favorites list
 * @memberof FavoritesService
 * @public
 */
async function removeFromFavorites(sku) {
    let url = `${getServiceUrl()}/${sku}?cntry=${getCountry()}`,
        response

    if (!isAwsMode()) {
        url += `&eid=${ServiceUtils.getUser().eid}`;
    }

    if (await isMarketEquinoxEnabled()) {
        await EquinoxService.removeItemInEquinoxList(sku)
        const equinoxFavoriteResponse = await EquinoxService.getEquinoxFavorite();
        response = EquinoxService.equinoxFavoritesMapper(equinoxFavoriteResponse)
    } else {
        response = await fetch(url, {
            method: 'DELETE',
            headers: getHeaders(),
            credentials: 'omit'
        });
    }

    return handleFavoritesListResponse(response);
}

/**
 * Returns a promise that resolves to true if the given SKU is in the user's
 * list of favorites, false otherwise.
 * @param {string} sku - SKU ID of product
 * @returns {Promise.<boolean>} Promise that resolves to boolean
 * @memberof FavoritesService
 * @public
 */
async function isInFavorites(sku) {
    let favorites = await getFavorites();
    return favorites.map(fav => fav.sku).includes(sku);
}

/**
 * Clears the user's favorites list data from storage.
 * @memberof FavoritesService
 * @public
 */
function clearLocalData() {
    storage.removeItem(storage.metadata.FAVORITES_LIST);
}

//=================================================================//
//
//  PRIVATE FUNCTIONS
//
//=================================================================//

/**
 * Retrieves the favorites list from local storage. Returns null if the list
 * cannot be found or if storage is unavailable.
 * @returns {Array.<Favorite>}
 * @memberof FavoritesService
 * @private
 */
function getFavoritesLocal() {
    if (favoritesList) {
        return favoritesList;
    }

    let data = storage.getItem(storage.metadata.FAVORITES_LIST);

    if (data && data.user === ServiceUtils.getUser().id
        && data.country === RunConfigService.getRunConfig().country) {
        return (data.favorites || []).map(fav => new Favorite(fav));
    }

    return null;
}

function setFavoritesLocal(favorites) {
    favoritesList = favorites;

    let data = {
        user: ServiceUtils.getUser().id,
        country: RunConfigService.getRunConfig().country,
        favorites: favorites
    };

    storage.setItem(storage.metadata.FAVORITES_LIST, data);
}

/**
 * Retrieves the favorites list from the server. The list is also saved to
 * local storage.
 * @returns {Promise.<Array.<Favorite>>} Promise that resolves to favorites list
 * @memberof FavoritesService
 * @private
 */
async function getFavoritesFromServer(options) {
    let url = `${getServiceUrl()}?cntry=${getCountry()}`,
        response;

    if (options && options.doAtp) {
        url += '&atp=true';
    }

    if (!isAwsMode()) {
        url += `&eid=${ServiceUtils.getUser().eid}`;
    }

    if(await isMarketEquinoxEnabled()) {

        const equinoxFavoriteResponse = await EquinoxService.getEquinoxFavorite();
        response = EquinoxService.equinoxFavoritesMapper(equinoxFavoriteResponse)

    } else {
        response = await fetch(url, {
            method: 'GET',
            headers: getHeaders(),
            credentials: 'omit'
        });
    }

    return handleFavoritesListResponse(response);
}

/**
 * Converts the raw Fetch response from the Favorites server to a favorites list,
 * saves the list to local storage, and returns the list
 * @param {Object} response - raw Fetch response
 * @returns {Promise.<Array.<Favorite>>} Promise that resolves to favorites list
 * @memberof FavoritesService
 * @private
 */
async function handleFavoritesListResponse(response) {
    let data = response;

    if(!await isMarketEquinoxEnabled()) {
        data = await ServiceUtils.transformFetchResponse(response);
    }

    if (!(FAVORITE_RESPONSE_LIST in data) || !(data[FAVORITE_RESPONSE_LIST] instanceof Array)) {
        throw new Error('Malformed Favorites List response from server');
    }

    let favorites = data[FAVORITE_RESPONSE_LIST].map(fav => new Favorite(fav));

    setFavoritesLocal(favorites);

    return favorites;
}

/**
 * Returns true if the market config specifies the use of the AWS cloud service.
 * Returns false otherwise.
 * @returns {boolean}
 * @memberof FavoritesService
 * @private
 */
function isAwsMode() {
    return getCountry() !== 'TW';
}

function getCountry() {
    return RunConfigService.getRunConfig().country;
}

function getServiceUrl() {
    let user = ServiceUtils.getUser();

    if (isAwsMode()) {
        let host = UrlService.getAwsUrl() || 'https://testapi.cloud.nuskin.com';
        return `${host}/favorites/v1/${user.id}`;
    } else {
        let host = UrlService.getSiteUrl() || 'https://test.nuskin.com';
        return `${host}/shop-service/api/v2/favorites/${user.id}`;
    }
}

function getHeaders() {
    const runConfig = RunConfigService.getRunConfig();

    let headers = {
        'Accept': 'application/json',
        'client_id': runConfig.clientId,
        'client_secret': runConfig.clientSecret
    };

    if (isAwsMode()) {
        headers['Authorization'] = `JWT ${ServiceUtils.getUser().eid}`;
    }

    return headers;
}

async function isMarketEquinoxEnabled() {
    const equinoxMarket = (await getConfiguration(["Equinox_Markets"])).Equinox_Markets;
    return equinoxMarket.active && equinoxLocalStorage.isEquinoxEnabled();
}
